
import React, { useState } from 'react';
import { Car, CarStatus } from '../types';
import { Link } from 'react-router-dom';
import { Gauge, Tag, ChevronRight, Fuel, ShieldCheck } from 'lucide-react';
import { TestDriveModal } from './TestDriveModal';

interface CarCardProps {
  car: Car;
}

export const CarCard: React.FC<CarCardProps> = ({ car }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(price);
  };

  const getStatusBadge = (status: CarStatus) => {
    switch (status) {
      case CarStatus.AVAILABLE:
        return <span className="bg-primary text-white text-[10px] px-2 py-0.5 rounded uppercase font-bold tracking-wider">Available</span>;
      case CarStatus.PENDING:
        return <span className="bg-accent text-brandDark text-[10px] px-2 py-0.5 rounded uppercase font-bold tracking-wider">Sale Pending</span>;
      case CarStatus.SOLD:
        return <span className="bg-danger text-white text-[10px] px-2 py-0.5 rounded uppercase font-bold tracking-wider">Sold</span>;
      default:
        return null;
    }
  };

  return (
    <>
      <div className="bg-white rounded-sm overflow-hidden shadow-md hover:shadow-2xl transition-all duration-300 border border-gray-100 flex flex-col h-full group">
        <Link to={`/inventory/${car.id}`} className="relative aspect-[16/10] overflow-hidden flex items-center justify-center bg-brandGray">
          <img
            src={car.images[0]}
            alt={`${car.year} ${car.make} ${car.model}`}
            className="w-full h-full object-contain group-hover:scale-105 transition-transform duration-700"
            loading="lazy"
          />
          <div className="absolute top-3 left-3 flex flex-col gap-1.5 items-start">
            {getStatusBadge(car.status)}
            {car.featured && <span className="bg-brandDark text-white text-[10px] px-2 py-0.5 rounded uppercase font-bold tracking-wider">Hot Pick</span>}
          </div>
        </Link>

        <div className="p-5 flex flex-col flex-grow bg-white">
          <div className="flex justify-between items-start mb-2">
            <Link to={`/inventory/${car.id}`}>
              <h3 className="font-heading text-xl font-bold text-brandDark hover:text-primary transition-colors uppercase leading-tight">
                {car.year} {car.make} {car.model}
              </h3>
            </Link>
          </div>
          
          <div className="flex items-center gap-4 text-xs text-gray-500 font-bold uppercase tracking-tight mb-4">
            <span className="flex items-center gap-1.5"><Gauge size={14} className="text-primary" /> {car.mileage.toLocaleString()} MI</span>
            <span className="flex items-center gap-1.5"><Tag size={14} className="text-accent" /> PRE-OWNED</span>
          </div>

          <div className="flex items-center justify-between mt-auto pt-4 border-t border-gray-100">
            <div className="flex flex-col">
              <span className="text-xs text-gray-400 uppercase font-bold">Today's Price</span>
              <span className="text-2xl font-heading font-bold text-brandDark">{formatPrice(car.price)}</span>
            </div>
            <Link 
              to={`/inventory/${car.id}`}
              className="bg-brandDark text-white p-2 hover:bg-primary transition-colors"
              title="View Details"
            >
              <ChevronRight size={24} />
            </Link>
          </div>
        </div>
        
        <button 
          onClick={() => setIsModalOpen(true)}
          className="w-full bg-brandGray py-3 text-xs font-bold uppercase tracking-widest text-brandDark hover:bg-accent transition-colors border-t border-gray-200"
        >
          Quick Appointment
        </button>
      </div>

      <TestDriveModal 
        isOpen={isModalOpen} 
        onClose={() => setIsModalOpen(false)} 
        carId={car.id} 
        carName={`${car.year} ${car.make} ${car.model}`} 
      />
    </>
  );
};
